import React, { FC, useEffect, useRef, useState } from 'react';

import { ApolloError } from '@apollo/client';
import { useGlobalSearchParams, useNavigation, useRouter } from 'expo-router';

import { CardModal } from 'components/card-modal';
import {
  ILoyaltyServiceProcessSmgCodeMutation,
  IProcessSmgCodeMutation,
  LoyaltySmgCodeStatus,
  useLoyaltyServiceProcessSmgCodeMutation,
  useProcessSmgCodeMutation,
} from 'generated/graphql-gateway';
import { useRoute } from 'hooks/navigation/use-route';
import { routes } from 'navigation/routing';
import { CustomEventNames, EventTypes, logEvent } from 'state/amplitude';
import { LaunchDarklyFlag, useFlag, useLaunchDarklyIdentified } from 'state/launchdarkly';
import { useLoyaltyClaimPointsContext } from 'state/loyalty-claim-points';
import { useLoyaltyUser, useLoyaltyUserId } from 'state/loyalty/hooks/use-loyalty-user';

import { LoadingAnimation } from './loyalty-claim-points.styled';
import { parseClaimMethod, resolveClaimRouteParams } from './resolve-claim-route-params';
import { ClaimMethod } from './types';
import { getBackUrl, previousRouteIsClaimResult } from './utils';

type ClaimPointsRouteParams = {
  code?: string | string[];
  transaction_date?: string | string[];
  method?: string | string[];
};

export enum LoyaltyCodeSubmittedStatus {
  COMPLETED = 'completed',
  FAILED = 'failed',
}

interface SmgCodeResult {
  pointsEarned: number;
  status: string;
  transactionId?: string | null;
}

const ALREADY_EARNED_STATUS = 'ALREADY_EARNED';

const LoyaltyClaimPointsProcess: FC<React.PropsWithChildren<unknown>> = () => {
  const { replace, canDismiss, dismiss, navigate } = useRouter();
  const navigation = useNavigation();
  const { loyaltyUser } = useLoyaltyUser();
  const { loyaltyUserId: authLoyaltyId } = useLoyaltyUserId();
  const { openModalWithStatus: onStatusChange } = useLoyaltyClaimPointsContext();
  const { params: localParams } = useRoute<ClaimPointsRouteParams>();
  const globalParams = useGlobalSearchParams<ClaimPointsRouteParams>();
  const { code, transaction_date, method: methodParam } = resolveClaimRouteParams(
    localParams,
    globalParams
  );
  const method = parseClaimMethod(methodParam);
  const methodName = method === undefined ? undefined : ClaimMethod[method];
  // Keep reading the live LD value until we actually fire. Submitting is irreversible, so the
  // legacy path - which is what the safe default selects - waits until LaunchDarkly has finished
  // identifying the user and the flag value can be trusted.
  const enableLoyaltyServiceSmg = Boolean(useFlag(LaunchDarklyFlag.ENABLE_LOYALTY_SERVICE_SMG));
  const launchDarklyIdentified = useLaunchDarklyIdentified();
  const hasSubmittedRef = useRef(false);
  const [networkFailure, setNetworkFailure] = useState(false);

  const smgCode = code;
  const transactionDate = transaction_date;
  const baseUrl = routes.claimPointsBase;
  const backUrl = getBackUrl(baseUrl, method);
  const acceptedHref = `${baseUrl}${routes.accepted}`;

  const staleClaimResultUnderneath = () =>
    previousRouteIsClaimResult(navigation.getState()?.routes);

  // "Enter another" pushes the form onto the previous result. Pop that result
  // too, so closing the next modal lands on claim-points.
  const openAccepted = (params: Record<string, string | number>) => {
    const href = { pathname: acceptedHref, params };

    if (staleClaimResultUnderneath()) {
      dismiss(2);
      navigate(href);
      return;
    }

    replace(href);
  };

  const returnFromProcessing = () => {
    if (staleClaimResultUnderneath()) {
      dismiss(2);
      return;
    }

    if (!canDismiss()) {
      replace(backUrl);
      return;
    }

    dismiss();
  };

  const onSmgCodeResult = (result: SmgCodeResult) => {
    logEvent(CustomEventNames.LOYALTY_CODE_SUBMITTED, EventTypes.UserContent, {
      Code: smgCode,
      Method: methodName,
      Status: result.status,
      Result: LoyaltyCodeSubmittedStatus.COMPLETED,
    });

    // Only status update not needed is `accepted` or same-guest already earned
    if (result.status === ALREADY_EARNED_STATUS) {
      return openAccepted({
        alreadyEarned: '1',
        earnedPoints: String(result.pointsEarned ?? 0),
        ...(result.transactionId ? { transactionId: result.transactionId } : {}),
      });
    }

    if (result.status !== LoyaltySmgCodeStatus.ACCEPTED) {
      onStatusChange(result.status);
      return returnFromProcessing();
    }

    if (result.pointsEarned) {
      return openAccepted({ earnedPoints: result.pointsEarned });
    }
  };

  const onLegacyMutationCompleted = (data: IProcessSmgCodeMutation) => {
    onSmgCodeResult(data.loyaltyProcessSmgCode);
  };

  const onServiceMutationCompleted = (data: ILoyaltyServiceProcessSmgCodeMutation) => {
    onSmgCodeResult(data.loyaltyServiceProcessSmgCode);
  };

  const onMutationError = (error: ApolloError) => {
    if (error?.graphQLErrors?.length) {
      const [err] = error.graphQLErrors;
      // @ts-expect-error TS(2571) FIXME: Object is of type 'unknown'.
      const status = err.extensions?.details?.status;
      onStatusChange(status);
      returnFromProcessing();

      logEvent(CustomEventNames.LOYALTY_CODE_SUBMITTED, EventTypes.UserContent, {
        Code: smgCode,
        Method: methodName,
        Status: status,
        Result: LoyaltyCodeSubmittedStatus.FAILED,
      });
    } else {
      logEvent(CustomEventNames.LOYALTY_CODE_SUBMITTED, EventTypes.UserContent, {
        Code: smgCode,
        Method: methodName,
        Status: 'UNKNOWN',
        Result: LoyaltyCodeSubmittedStatus.FAILED,
      });
      setNetworkFailure(true);
      returnFromProcessing();
    }
  };

  const [processLegacyCode, { loading: processingLegacySmgCode }] = useProcessSmgCodeMutation({
    onError: onMutationError,
    onCompleted: onLegacyMutationCompleted,
  });

  const [
    processServiceCode,
    { loading: processingServiceSmgCode },
  ] = useLoyaltyServiceProcessSmgCodeMutation({
    onError: onMutationError,
    onCompleted: onServiceMutationCompleted,
  });

  const loyaltyUserId = loyaltyUser?.id ?? authLoyaltyId;
  const processingSmgCode = processingLegacySmgCode || processingServiceSmgCode;

  useEffect(() => {
    if (!smgCode || hasSubmittedRef.current) {
      return;
    }

    if (enableLoyaltyServiceSmg) {
      hasSubmittedRef.current = true;
      processServiceCode({
        variables: {
          smgCode,
          ...(transactionDate && { transactionDate }),
        },
      });
      return;
    }

    if (launchDarklyIdentified && loyaltyUserId) {
      hasSubmittedRef.current = true;
      processLegacyCode({
        variables: {
          loyaltyId: loyaltyUserId,
          smgCode,
          ...(transactionDate && { transactionDate }),
        },
      });
    }
  }, [
    enableLoyaltyServiceSmg,
    launchDarklyIdentified,
    loyaltyUserId,
    processLegacyCode,
    processServiceCode,
    smgCode,
    transactionDate,
  ]);

  if (networkFailure) {
    return null;
  }

  if (processingSmgCode || !hasSubmittedRef.current) {
    return (
      <CardModal>
        <LoadingAnimation />
      </CardModal>
    );
  }

  return null;
};

export default LoyaltyClaimPointsProcess;
